import { AirCurtain } from 'src/objectModelV2/airCurtain';
import {
    MEASUREMENTS,
    INTERVALS
} from '../api/reporting';
import { getCachedData, MEASUREMENT_TO_FUNC } from '../api/dataManagementApi';
import { isTimeStampLast24hrs, getTempUnitCelsiusSelected } from './ui';
import { models } from '../../assets/air_curtain_models.json';

export const AGGREGATES_KEY = 'aggregates';

export const COLUMN_CHART_MEASUREMENTS = [MEASUREMENTS.BTU, MEASUREMENTS.LOSS, MEASUREMENTS.COST_TO_OPERATE, 
    MEASUREMENTS.GROSS_SAVING, MEASUREMENTS.SAVING, MEASUREMENTS.TON_GAIN_RATE, MEASUREMENTS.DOOR_OPEN_TIME, MEASUREMENTS.DOOR_OPEN_COUNT];

export const AREA_GRAPH_MEASUREMENTS = [MEASUREMENTS.AVERAGE_TEMP, MEASUREMENTS.MAX_TEMP, MEASUREMENTS.MIN_TEMP, MEASUREMENTS.WEATHER_AVG_TEMP, MEASUREMENTS.EFFICIENCY];

export const MEASUREMENTS_LACKING_24HR_DATA = [MEASUREMENTS.EFFICIENCY, MEASUREMENTS.SAVING, MEASUREMENTS.GROSS_SAVING, MEASUREMENTS.BTU, MEASUREMENTS.TON_GAIN_RATE, 
    MEASUREMENTS.LOSS, MEASUREMENTS.COST_TO_OPERATE, MEASUREMENTS.DOOR_OPEN_TIME, MEASUREMENTS.WEATHER_AVG_TEMP ];

export const LOCALLY_CALCULATED_MEASUREMENTS = [MEASUREMENTS.LOSS, MEASUREMENTS.COST_TO_OPERATE, MEASUREMENTS.SAVING, MEASUREMENTS.GROSS_SAVING];

export const SITE_SPECIFIC_MEASUREMENTS = [MEASUREMENTS.WEATHER_AVG_TEMP];

export async function getInfographicDataAggregated(
    selectedRange: string,
    measurement: string,
    average: boolean,
    airCurtains: AirCurtain[]
): Promise<InfographicData> {
    const aircurtainIds = airCurtains.map((aircurtain: AirCurtain) => {
        return aircurtain.airCurtainID;
    });
    const aggMeasurementKey = MEASUREMENT_TO_FUNC.get(measurement) + '(' + measurement + ')';
    const oldData = await getCachedData(selectedRange, aircurtainIds, [measurement], true, true, "");

    // Data from the period preceding the range, i.e. week before last, to check change amount
    const oldDataAggregated = oldData.get(AGGREGATES_KEY)?.get(aggMeasurementKey);
    let oldValue = getTotal(oldDataAggregated) ?? 0;
    if (average) {
        oldValue = oldDataAggregated?.length ? oldValue / oldDataAggregated.length : 0;
    }

    //Data from the current range, i.e. last week
    const newData = await getCachedData(selectedRange, aircurtainIds, [measurement], true, false, "");
    const newDataAggregated = newData.get(AGGREGATES_KEY)?.get(aggMeasurementKey);
    let value = newDataAggregated?.length ? Math.ceil(getTotal(newDataAggregated)) : undefined;

    if (average) {
        value = newDataAggregated?.length ?  value / newDataAggregated.length : undefined;
    }
    // TODO: shouldn't return a value until old data is cached
    return {
        value,
        change: value === undefined  ? 0 : value - oldValue,
    };
}

export async function getInfographicData(
    selectedRange: string,
    measurement: string,
    average: boolean,
    airCurtains: AirCurtain[]
): Promise<Map<string, InfographicData>> {
    const aircurtainIds = airCurtains.map((aircurtain: AirCurtain) => {
        return aircurtain.airCurtainID;
    });

    const results = new Map<string, InfographicData>();
    for (const deviceId of aircurtainIds) {
        const dataByMeasurement = await getCachedData(selectedRange, [deviceId], [measurement], false, false, "");
        const oldDataByMeasurement = await getCachedData(selectedRange, [deviceId], [measurement], false, true, "");

        results.set(deviceId, null);
        const deviceData = dataByMeasurement.get(deviceId);
        const oldDeviceData = oldDataByMeasurement.get(deviceId);
        if (deviceData) {
            const data = deviceData.get(measurement);
            const oldData = oldDeviceData?.get(measurement);

            if (data && data.length) {
                let value = Math.ceil(getTotal(data));
                let oldValue = 0;
                if (average) {
                    value = value / Object.keys(data).length;
                }

                if (oldData) {
                    oldValue = Math.ceil(getTotal(oldData));
                    if (average) {
                        oldValue = oldValue / Object.keys(oldData).length;
                    }        
                }

                const infoGraphicData = {
                    value,
                    change: value === undefined ? 0 : value - oldValue,
                };

                results.set(deviceId, infoGraphicData);
            } 
        }
    }

    return results;
}

export function getTotal(graphData: GraphData[]): number {
    let value = 0;
    value = graphData?.reduce(
        (
            accumulator: number,
            currentValue: GraphData,
            currentIndex: number,
            data: GraphData[]
        ) => {
            return accumulator + data[currentIndex].value;
        },
        0
    );

    return value;
}

export function getAverage(data: GraphData[], measurement: string, numDates: number): number {
    let average = 0;
    let values = [];
    let lastValidValue = 0;
    values = data?.map((gData: GraphData) => {
        // compensate for data not yet processed within last 24 hrs
        let value = gData.value;
        if (isTimeStampLast24hrs(gData.timestamp) && MEASUREMENTS_LACKING_24HR_DATA.includes(measurement)) {
            value = lastValidValue;
        } else {
            lastValidValue = gData.value;
        }
        return getDisplayValue(measurement, value);
    });
    const divisor = (numDates > 0 ? numDates : values.length);
    if (values?.length) {
        average = values.reduce((acc: number, current: number) => {
            return (acc += current);
        }) / divisor;
    }

    return average;
}

export function getMaxMin(
    data: GraphData[],
    measurement: string
): { max: number; min: number } {
    let values = [];
    let lastValidValue = 0;
    values = data?.map((gData: any) => {
        let value = gData.value;
        // compensate for data not yet processed within last 24 hrs
        if (isTimeStampLast24hrs(gData.timestamp) && MEASUREMENTS_LACKING_24HR_DATA.includes(measurement)) {
            value = lastValidValue;
        } else {
            lastValidValue = gData.value;
        }

        return getDisplayValue(measurement, value);
    });

    const sorted = values?.length ? values.sort((a, b) => { return a - b; }) : [0,0];
    return { max: sorted[sorted.length - 1], min: sorted[0] };
}

export function getCoolingCost(electricityRate: number, naturalGasRate: number, oilRate: number, energyType: string): number {
    switch (energyType){
        case 'e':
            return electricityRate;
        case 'g':
            return naturalGasRate * 0.974966278 / 0.99;
        case 'o':
            return 0;
    } 
}

export function getHeatingCost(electricityRate: number, naturalGasRate: number, oilRate: number, energyType: string): number {
    switch (energyType){
        case 'e':
            return electricityRate * 1000 / 3.413;
        case 'g':
            return naturalGasRate * 0.974966278;
        case 'o':
            return oilRate * 0.172323997;
    }  
}

function getLoss(btu: number, tonGainRate: number, coolingCost: number, heatingCost: number): number {    
    return (btu * heatingCost + tonGainRate * coolingCost);
}

function getCostToOperate(modelId: string, electricityRate: number, doorOpenHours: number): number {
    const modelsStruct = JSON.parse(JSON.stringify(models));
    const modelData = modelsStruct[`${modelId}`];
  
    if (modelData === undefined) {
        return 0;
    } 
    
    const totalHorsePower = modelData.totalHorsePower;
    if (totalHorsePower === undefined) {
        return 0;
    } 
    
    return (0.746 * totalHorsePower * electricityRate * doorOpenHours);  
}

function getSaving(grossSaving: number, costToOperate: number): number {
    return grossSaving - costToOperate;
}

function precise_round(num: any, decimals: any): string {
    const sign = num >= 0 ? 1 : -1;
    return (Math.round((num*Math.pow(10,decimals)) + (sign*0.001)) / Math.pow(10,decimals)).toFixed(decimals);
}

export function getSavingData(grossSavingData: GraphData[], costToOperateData: GraphData[]): GraphData[] {
    const timeStamps = grossSavingData?.map((graphData) => { return graphData.timestamp; });
    const results: GraphData[] = [];

    for (const timestamp of timeStamps) {
        const grossSaving = grossSavingData.find((graphData) => {
            return graphData.timestamp === timestamp;
        })?.value;

        const costToOperate = costToOperateData.find((graphData) => {
            return graphData.timestamp === timestamp;
        })?.value;

        if (grossSaving !== undefined && costToOperate !== undefined) {
            const value = getSaving(grossSaving, costToOperate);
            results.push({timestamp, value});
        }
    }
    
    return results;
} 

export function getCostToOperateData(doorOpenTimeData: GraphData[], modelId: string, electricityRate: number): GraphData[] {
    const timeStamps = doorOpenTimeData?.map((doorOpenTimeData) => { return doorOpenTimeData.timestamp; });
    const results: GraphData[] = [];

    for (const timestamp of timeStamps) {
        const doorOpenHours = doorOpenTimeData.find((graphData) => {
            return graphData.timestamp === timestamp;
        })?.value;

        if (doorOpenHours !== undefined) {
            const value = getCostToOperate(modelId, electricityRate, doorOpenHours);
            results.push({timestamp, value});
        }
    }

    return results;
}

export function getLossData(btuData: GraphData[], tonGainRateData: GraphData[], coolingCost: number, heatingCost: number): GraphData[] {
    const timeStamps = btuData?.map((graphData) => { return graphData.timestamp; });
    const results: GraphData[] = [];

    for (const timestamp of timeStamps) {
        const btu = btuData.find((graphData) => {
            return graphData.timestamp === timestamp;
        })?.value;

        const tonGainRate = tonGainRateData.find((graphData) => {
            return graphData.timestamp === timestamp;
        })?.value;

        if (btu !== undefined && tonGainRate !== undefined) {
            const value = getLoss(btu, tonGainRate, coolingCost, heatingCost);
            results.push({timestamp, value});
        }
    }
    
    return results;
}

export function getGrossSavingData(efficiencyData: GraphData[], btuData: GraphData[], tonGainRateData: GraphData[], coolingCost: number, heatingCost: number): GraphData[] {
    const timeStamps = efficiencyData?.map((graphData) => { return graphData.timestamp; });
    const results: GraphData[] = [];

    for (const timestamp of timeStamps) {
        const btu = btuData.find((graphData) => {
            return graphData.timestamp === timestamp;
        })?.value;

        const tonGainRate = tonGainRateData.find((graphData) => {
            return graphData.timestamp === timestamp;
        })?.value;

        const efficiency = efficiencyData.find((graphData) => {
            return graphData.timestamp === timestamp;
        })?.value;

        if (btu !== undefined && tonGainRate !== undefined && efficiency !== undefined) {
            const loss = getLoss(btu, tonGainRate, coolingCost, heatingCost);
            const value = loss * efficiency;
            if (value !== undefined) {
                results.push({timestamp, value});
            }
        }
    }

    return results;
}

export function getConvertedTemperature(value: number, decimalPoints = 1): number {
    if (getTempUnitCelsiusSelected()) {
        return convertFahrenheitToCelsius(value, decimalPoints);
    } else {
        return +precise_round(value, decimalPoints);
    }
}

function convertFahrenheitToCelsius(value: number, decimalPoints = 1): number {
    const result = ((value - 32) * .5556);
    return +precise_round(result, decimalPoints);
}

export function convertCelsiusToFahrenheit(value: number): number {
    const result = ((value * 9/5) + 32);
    return +precise_round(result, 1);
}

export function getDisplayValue(measurement: string, value: number): number {
    switch (measurement) {
        case MEASUREMENTS.EFFICIENCY:
            return value * 100;
        case MEASUREMENTS.DOOR_OPEN_COUNT:
            return Math.ceil(value);
        case MEASUREMENTS.DOOR_OPEN_TIME:
            return (value < 1) ? Math.ceil(value * 60) : Math.ceil(value);
        case MEASUREMENTS.SAVING:
        case MEASUREMENTS.GROSS_SAVING:
            // if location / installation are misconfigured negative values are possible
            return value < 0 ? 0 : value;
        case MEASUREMENTS.AVERAGE_TEMP:
        case MEASUREMENTS.MIN_TEMP:
        case MEASUREMENTS.MAX_TEMP:
        case MEASUREMENTS.WEATHER_AVG_TEMP:
            return getConvertedTemperature(value); 
        default:
            return value;
    }
}

export function getMeasurementUI(
    measurement: string,
    interval = ''
): GraphUI {
    const graphUI = {
        label: '',
        yAxisLabel: '',
        color: 'lightblue',
        colorSecondary: 'midnightblue',
        colorAlpha: 'lightblue',
        unit: '',
        decimals: 0,
        icon: '',
        subtitle: '',
        yAxisMin: 0,
        yAxisMax: 0,
        yAxisRangePercentLimit: 0,
        displayTotal: false,
    };

    const redAlpha = 'rgba(255,95,76,0.05)';
    const blueAlpha = 'rgba(74,144,226,0.05)';
    const greenAlpha = 'rgba(67,185,41,0.05)';
    const greyAlpha = 'rgba(80,80,80,0.05)';
    const orangeAlpha = 'rgba(255,165,0,0.05)';

    switch (measurement) {
        case MEASUREMENTS.AVERAGE_TEMP:
            graphUI.label = 'Temperature';
            graphUI.color = '#ff5f4c';
            graphUI.colorSecondary = 'tomato';
            graphUI.colorAlpha = redAlpha;
            graphUI.yAxisLabel = 'Degrees';
            graphUI.unit = getTempUnitCelsiusSelected() ? '°C' : '°F';
            graphUI.yAxisRangePercentLimit = 5;
            graphUI.decimals = 1
            break;
        case MEASUREMENTS.MAX_TEMP:
            graphUI.label = 'Max Temperature';
            graphUI.color = '#ff5f4c';
            graphUI.colorSecondary = 'tomato';
            graphUI.colorAlpha = redAlpha;
            graphUI.yAxisLabel = 'Degrees';
            graphUI.unit = getTempUnitCelsiusSelected() ? '°C' : '°F';
            graphUI.yAxisRangePercentLimit = 5;
            graphUI.decimals = 1
            break;
        case MEASUREMENTS.MIN_TEMP:
            graphUI.label = 'Min Temperature';
            graphUI.color = '#ff5f4c';
            graphUI.colorSecondary = 'tomato';
            graphUI.colorAlpha = redAlpha;
            graphUI.yAxisLabel = 'Degrees';
            graphUI.unit = getTempUnitCelsiusSelected() ? '°C' : '°F';
            graphUI.yAxisRangePercentLimit = 5;
            graphUI.decimals = 1
            break;
        case MEASUREMENTS.WEATHER_AVG_TEMP:
            graphUI.label = 'Outside Temperature';
            graphUI.color = 'orange';
            graphUI.colorSecondary = 'red';
            graphUI.colorAlpha = orangeAlpha;
            graphUI.yAxisLabel = 'Degrees';
            graphUI.unit = getTempUnitCelsiusSelected() ? '°C' : '°F';
            graphUI.yAxisRangePercentLimit = 5;
            graphUI.decimals = 1
            break;
        case MEASUREMENTS.EFFICIENCY:
            graphUI.label = 'Energy Saving';
            graphUI.color = '#43b929';
            graphUI.colorSecondary = 'mediumseagreen';
            graphUI.colorAlpha = greenAlpha;
            graphUI.yAxisLabel = 'Percent';
            graphUI.unit = '%';
            graphUI.yAxisMax = 100;
            break;
        case MEASUREMENTS.SAVING:
            graphUI.label = 'Saving';
            graphUI.color = 'royalblue';
            graphUI.colorSecondary = 'midnightblue';
            graphUI.colorAlpha = blueAlpha;
            graphUI.decimals = 2;
            graphUI.yAxisLabel = 'Dollars';
            graphUI.unit = '$';
            graphUI.displayTotal = true;
            break;
        case MEASUREMENTS.COST_TO_OPERATE:
            graphUI.label = 'Cost to Operate';
            graphUI.color = 'royalblue';
            graphUI.colorSecondary = 'midnightblue';
            graphUI.colorAlpha = blueAlpha;
            graphUI.decimals = 2;
            graphUI.yAxisLabel = 'Dollars';
            graphUI.unit = '$';
            graphUI.yAxisRangePercentLimit = 5;
            graphUI.displayTotal = true;
            break;
        case MEASUREMENTS.GROSS_SAVING:
            graphUI.label = 'Gross Saving';
            graphUI.color = 'royalblue';
            graphUI.colorSecondary = 'midnightblue';
            graphUI.colorAlpha = blueAlpha;
            graphUI.decimals = 2;
            graphUI.yAxisLabel = 'Dollars';
            graphUI.unit = '$';
            graphUI.yAxisRangePercentLimit = 5;
            graphUI.displayTotal = true;
            break;
        case MEASUREMENTS.BTU:
            graphUI.label = 'BTU';
            graphUI.color = 'pink';
            graphUI.colorSecondary = 'tomato';
            graphUI.colorAlpha = redAlpha;
            graphUI.decimals = 6;
            graphUI.yAxisLabel = 'BTU';
            graphUI.unit = 'Btu';
            graphUI.subtitle = 'Heat energy saved by the air curtain(s)';
            break;
        case MEASUREMENTS.TON_GAIN_RATE:
            graphUI.label = 'Ton Gain Rate';
            graphUI.color = 'pink';
            graphUI.colorSecondary = 'tomato';
            graphUI.colorAlpha = redAlpha;
            graphUI.decimals = 3;
            graphUI.yAxisLabel = '';
            graphUI.unit = 'TR';
            graphUI.subtitle = 'Cooling energy lost';
            graphUI.yAxisRangePercentLimit = 5;
            break;
        case MEASUREMENTS.LOSS:
            graphUI.label = 'Loss';
            graphUI.color = 'lightsteelblue';
            graphUI.colorSecondary = 'lightslategrey';
            graphUI.colorAlpha = greyAlpha;
            graphUI.decimals = 2;
            graphUI.yAxisLabel = 'Dollars';
            graphUI.unit = '$';
            graphUI.displayTotal = true;
            // graphUI.subtitle = 'Combined cooling and heating cost saved by the air curtain(s)';
            break;
        case MEASUREMENTS.DOOR_OPEN_TIME:
            graphUI.label = 'Door Open Time';
            graphUI.color = 'royalblue';
            graphUI.yAxisMax = 100;
            (graphUI.yAxisLabel = 'Time'),
                (graphUI.unit = interval === INTERVALS.HOURLY ? 'min' : 'hour');
            break;
        case MEASUREMENTS.DOOR_OPEN_COUNT:
            graphUI.label = 'Doors Open';
            graphUI.color = '#4a90e2';
            (graphUI.colorSecondary = 'cornflowerblue'),
                (graphUI.yAxisLabel = 'Number');
            graphUI.unit = '';
            graphUI.displayTotal = true;
            break;
    }

    return graphUI;
}
